import { getName } from '@tauri-apps/api/app';
import { join } from '@tauri-apps/api/path';
import { exists } from '@tauri-apps/plugin-fs';
import { check, type Update } from '@tauri-apps/plugin-updater';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import {
  isInApplicationsFolderAtom,
  otaUpdateDataAtom,
  updateChannelPreferenceAtom,
} from '../pique/setting-modal/atoms';
import {
  disposeUpdate,
  isReleaseMetadataMissingError,
  OTA_UPDATE_POLL_INTERVAL_MS,
} from './ota-update-utils';

export default function OtaUpdateMonitor() {
  const autoCheckEnabled = useAtomValue(updateChannelPreferenceAtom);
  const setUpdateData = useSetAtom(otaUpdateDataAtom);
  const setIsInApplicationsFolder = useSetAtom(isInApplicationsFolderAtom);
  const trackedVersionRef = useRef<string | null>(null);
  const latestUpdateRef = useRef<Update | null>(null);
  const inFlightCheckRef = useRef(false);
  const pollTimeoutRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  const runUpdateCheckRef = useRef(
    async (_reason: 'mount' | 'poll' | 'focus' | 'visibility' | 'online') => {}
  );

  const clearScheduledCheck = useCallback(() => {
    if (pollTimeoutRef.current !== null) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const replaceTrackedUpdate = useCallback(
    (nextUpdate: Update | null) => {
      const previousUpdate = latestUpdateRef.current;
      latestUpdateRef.current = nextUpdate;
      setUpdateData(nextUpdate);

      if (previousUpdate && previousUpdate !== nextUpdate) {
        disposeUpdate(previousUpdate, 'replacing update result').catch(
          error => {
            console.error('Error disposing previous update handle:', error);
          }
        );
      }

      if (nextUpdate?.version) {
        if (trackedVersionRef.current !== nextUpdate.version) {
          trackedVersionRef.current = nextUpdate.version;
        }
      } else {
        trackedVersionRef.current = null;
      }
    },
    [setUpdateData]
  );

  const clearTrackedUpdate = useCallback(() => {
    replaceTrackedUpdate(null);
  }, [replaceTrackedUpdate]);

  const scheduleNextCheck = useCallback(() => {
    clearScheduledCheck();
    if (!mountedRef.current || !autoCheckEnabled) {
      return;
    }

    pollTimeoutRef.current = window.setTimeout(() => {
      pollTimeoutRef.current = null;
      runUpdateCheckRef.current('poll').catch(error => {
        console.error('Error during scheduled update check:', error);
      });
    }, OTA_UPDATE_POLL_INTERVAL_MS);
  }, [autoCheckEnabled, clearScheduledCheck]);

  const runUpdateCheck = useCallback(
    async (reason: 'mount' | 'poll' | 'focus' | 'visibility' | 'online') => {
      if (
        !mountedRef.current ||
        !autoCheckEnabled ||
        inFlightCheckRef.current
      ) {
        return;
      }

      inFlightCheckRef.current = true;

      try {
        const result = await check();

        if (!mountedRef.current) {
          await disposeUpdate(result, `abandoned ${reason} update check`);
          return;
        }

        replaceTrackedUpdate(result);
      } catch (error) {
        if (!mountedRef.current) {
          return;
        }

        if (!isReleaseMetadataMissingError(error)) {
          console.error(`Error checking for updates during ${reason}:`, error);
        }
      } finally {
        inFlightCheckRef.current = false;
        scheduleNextCheck();
      }
    },
    [autoCheckEnabled, replaceTrackedUpdate, scheduleNextCheck]
  );

  runUpdateCheckRef.current = runUpdateCheck;

  useEffect(() => {
    mountedRef.current = true;

    const checkFolder = async () => {
      try {
        const name = await getName();
        const path = await join('/Applications', `${name}.app`);
        const result = await exists(path);
        setIsInApplicationsFolder(result);
      } catch (error) {
        console.error('Error checking Applications folder:', error);
        setIsInApplicationsFolder(false);
      }
    };

    checkFolder().catch(console.error);

    return () => {
      mountedRef.current = false;
      clearScheduledCheck();
      disposeUpdate(latestUpdateRef.current, 'unmount cleanup').catch(error => {
        console.error('Error disposing update handle during unmount:', error);
      });
      latestUpdateRef.current = null;
    };
  }, [clearScheduledCheck, setIsInApplicationsFolder]);

  useEffect(() => {
    if (!autoCheckEnabled) {
      clearScheduledCheck();
      inFlightCheckRef.current = false;
      clearTrackedUpdate();
      return;
    }

    const handleFocus = () => {
      runUpdateCheck('focus').catch(error => {
        console.error('Error checking for updates on focus:', error);
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        runUpdateCheck('visibility').catch(error => {
          console.error(
            'Error checking for updates on visibility change:',
            error
          );
        });
      }
    };
    const handleOnline = () => {
      runUpdateCheck('online').catch(error => {
        console.error('Error checking for updates after reconnect:', error);
      });
    };

    runUpdateCheck('mount').catch(error => {
      console.error('Error checking for updates on mount:', error);
    });
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      clearScheduledCheck();
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [
    autoCheckEnabled,
    clearScheduledCheck,
    clearTrackedUpdate,
    runUpdateCheck,
  ]);

  return null;
}
